import { useEffect, useRef } from 'react';
import { Track, type RemoteTrack } from 'livekit-client';
import { applySink } from '../lib/useLocalMedia';
import { useFullscreen } from '../lib/useFullscreen';
import { usePointerIdle } from '../lib/usePointerIdle';
import { useAudioLevel } from '../lib/useAudioLevel';
import { FullscreenIcon, MicIcon } from './Icons';
import { MicMeter } from './MicMeter';

interface Props {
  name: string;
  /** The local tile's own capture. Remote tiles pass `tracks` instead. */
  stream?: MediaStream | null;
  /** A remote participant's LiveKit tracks. These are attached rather than
      unwrapped into a MediaStream — adaptive stream only sizes what it can
      see attached. See useRoomMedia. */
  tracks?: RemoteTrack[];
  videoEnabled: boolean;
  audioEnabled: boolean;
  /** Local tile: mirrored, and muted so the user does not hear themselves. */
  isSelf?: boolean;
  speaking?: boolean;
  /** Output device for this tile's audio. Ignored on the local tile, which is
      muted anyway. */
  sinkId?: string;
  /** The local microphone, so a tile filling the screen can still offer the
      mute button — the call's control bar is not on screen at that point.
      Always your own microphone, on someone else's tile as much as your own:
      that is what every other control in a call means. */
  selfAudioEnabled?: boolean;
  onToggleSelfAudio?: () => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

export function VideoTile({
  name,
  stream,
  tracks,
  videoEnabled,
  audioEnabled,
  isSelf,
  speaking,
  sinkId,
  selfAudioEnabled,
  onToggleSelfAudio,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const fullscreen = useFullscreen(tileRef);
  /* Only while this tile owns the screen. In the mosaic the bar is part of the
     layout and fading it on an idle mouse would just make the grid flicker. */
  const idle = usePointerIdle(fullscreen.active);

  const hasVideo = tracks
    ? tracks.some((t) => t.kind === Track.Kind.Video)
    : (stream?.getVideoTracks().length ?? 0) > 0;

  /* The meter measures the track itself rather than anything the SFU reports,
     so it works the same for the local capture and for a remote participant.
     Muting the local microphone flips `enabled`, which the analyser reads as
     silence anyway — but the meter is dropped for the mic-off icon at that
     point regardless, so nothing is left running behind it. */
  const audioTrack = tracks
    ? tracks.find((t) => t.kind === Track.Kind.Audio)?.mediaStreamTrack
    : stream?.getAudioTracks()[0];
  const level = useAudioLevel(audioTrack, audioEnabled);

  // The local capture is a plain MediaStream and goes straight on the element.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || tracks) return;
    if (el.srcObject !== (stream ?? null)) el.srcObject = stream ?? null;
    // Autoplay can still be refused when the tab has had no user gesture;
    // there is nothing to do about it here beyond not throwing.
    el.play().catch(() => {});
  }, [stream, tracks]);

  /* Remote tracks go on through LiveKit. `attach` is what registers the element
     with adaptive stream, which is how the SFU learns how big this tile is and
     therefore which simulcast layer to send. Detaching on the way out is what
     tells it to stop sending at all. */
  const attachedRef = useRef<RemoteTrack[]>([]);
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !tracks) return;
    /* Only the difference is applied. The audio and video tracks of one
       participant are subscribed a moment apart, and detaching both to
       reattach both would blink the picture when the second one lands. */
    for (const track of attachedRef.current) {
      if (!tracks.includes(track)) track.detach(el);
    }
    for (const track of tracks) {
      if (!attachedRef.current.includes(track)) track.attach(el);
    }
    attachedRef.current = tracks;
  }, [tracks]);

  useEffect(
    () => () => {
      const el = videoRef.current;
      if (el) attachedRef.current.forEach((track) => track.detach(el));
      attachedRef.current = [];
    },
    [],
  );

  // Remote audio plays through this element, so the speaker choice is applied
  // per tile. Reapplied when the tracks change: a fresh element defaults back
  // to the system output.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || isSelf) return;
    void applySink(el, sinkId);
  }, [sinkId, stream, tracks, isSelf]);

  return (
    /* Double-click promotes one person to the whole screen — the gesture people
       already expect from a video player. The control bar keeps the
       keyboard-reachable route to fullscreen for the call as a whole.

       Deliberately no `title`. A native tooltip on a tile is a tooltip on the
       video itself: the pointer has nowhere else to rest during a call, so it
       surfaced constantly, and in fullscreen it sat over the picture every time
       the mouse moved. The gesture is a video-player convention and does not
       need announcing at that price. */
    <div
      ref={tileRef}
      className={`tile${isSelf ? ' mirrored' : ''}${speaking ? ' tile-speaking' : ''}${idle ? ' idle' : ''}`}
      onDoubleClick={fullscreen.supported ? fullscreen.toggle : undefined}
    >
      <video ref={videoRef} playsInline autoPlay muted={isSelf} />
      {/* A participant can be live but carry audio only — the camera is off, or
          a background effect has not warmed up yet. Reading the track list here
          is safe: any change to it arrives with a re-render from above. */}
      {!videoEnabled || !hasVideo ? (
        <div className="tile-off">
          <div className="avatar">{initials(name)}</div>
        </div>
      ) : null}
      {/* stopPropagation so a quick second click on a button is not also read
          as the double-click that drops out of fullscreen. */}
      {fullscreen.active ? (
        <div className="tile-controls" onDoubleClick={(e) => e.stopPropagation()}>
          {onToggleSelfAudio ? (
            <button
              className="control"
              aria-pressed={selfAudioEnabled}
              onClick={onToggleSelfAudio}
              title={
                selfAudioEnabled ? 'Mute my microphone (space)' : 'Unmute my microphone (space)'
              }
            >
              <MicIcon off={!selfAudioEnabled} />
              <span className="visually-hidden">
                {selfAudioEnabled ? 'Mute my microphone' : 'Unmute my microphone'}
              </span>
            </button>
          ) : null}
          <button
            className="control neutral"
            aria-pressed
            onClick={fullscreen.toggle}
            title="Exit fullscreen"
          >
            <FullscreenIcon on />
            <span className="visually-hidden">Exit fullscreen</span>
          </button>
        </div>
      ) : null}

      <div className="tile-bar">
        <span className={`dot${audioEnabled ? ' on' : ''}`} aria-hidden="true" />
        <span className="tile-name">
          {name}
          {isSelf ? ' (you)' : ''}
        </span>
        {audioEnabled ? (
          <MicMeter segments={level} />
        ) : (
          <span style={{ width: 16, height: 16, marginLeft: 'auto' }} title={`${name} is muted`}>
            <MicIcon off />
          </span>
        )}
      </div>
    </div>
  );
}
